<template>
  <div class="container-fluid h-100 p-0">
    <div class="row h-100 g-0">
      <!-- Left Side Form -->
      <div class="col-12 col-md-3 col-lg-3 h-100">
        <div
          class="overflow-y-auto h-100 pt-4 px-3"
          style="height: calc(100vh - 60px)"
        >
          <!-- Project Details Section -->
          <section class="mb-3 pb-3 border-bottom">
            <h3 class="mb-3">{{ $t("project.info.title") }}</h3>
            <div class="mb-3">
              <label class="form-label">{{ $t("project.info.name") }}</label>
              <input
                type="text"
                v-model="project.project_name"
                class="form-control"
              />

              <label class="form-label mt-3">{{ $t("project.info.id") }}</label>
              <input
                type="text"
                :value="
                  'id' in project
                    ? String(project.id)
                    : 'Will be generated after saving'
                "
                class="form-control"
                disabled
              />

              <label class="form-label mt-3">{{
                $t("project.info.serviceType")
              }}</label>
              <select v-model="project.service_type" class="form-select">
                <option value="product_supply">
                  {{ $t("project.info.serviceTypes.productSupply") }}
                </option>
                <option value="product_supply_commissioning">
                  {{
                    $t("project.info.serviceTypes.productSupplyCommissioning")
                  }}
                </option>
                <option value="full_service">
                  {{ $t("project.info.serviceTypes.fullService") }}
                </option>
                <option value="complete_solution">
                  {{ $t("project.info.serviceTypes.completeSolution") }}
                </option>
              </select>

              <label class="form-label mt-3"
                >{{ $t("project.info.temperature") }} (°C)</label
              >
              <div class="row g-2">
                <div class="col">
                  <input
                    type="number"
                    v-model="project.temperature_min"
                    :placeholder="$t('project.info.temperatureMin')"
                    class="form-control"
                    step="1"
                    @input="
                      project.temperature_min = String(
                        Math.round(Number(project.temperature_min))
                      )
                    "
                  />
                </div>
                <div class="col">
                  <input
                    type="number"
                    v-model="project.temperature_avg"
                    :placeholder="$t('project.info.temperatureNormal')"
                    class="form-control"
                    step="1"
                    @input="
                      project.temperature_avg = String(
                        Math.round(Number(project.temperature_avg))
                      )
                    "
                  />
                </div>
                <div class="col">
                  <input
                    type="number"
                    v-model="project.temperature_max"
                    :placeholder="$t('project.info.temperatureMax')"
                    class="form-control"
                    step="1"
                    @input="
                      project.temperature_max = String(
                        Math.round(Number(project.temperature_max))
                      )
                    "
                  />
                </div>
              </div>
            </div>
          </section>

          <!-- Client Data Section -->
          <section class="mb-3 pb-3 border-bottom">
            <h3 class="mb-3">
              {{ $t("project.info.clientData") }}
            </h3>
            <div class="mb-3">
              <input
                type="text"
                v-model="project.client_name"
                :placeholder="$t('project.info.clientName')"
                class="form-control mb-2"
              />
              <input
                type="email"
                v-model="project.client_email"
                :placeholder="$t('project.info.clientEmail')"
                class="form-control mb-2"
              />
              <input
                type="text"
                v-model="project.client_phone"
                :placeholder="$t('project.info.clientPhone')"
                class="form-control mb-2"
              />
              <label class="form-label">{{
                $t("project.info.clientType")
              }}</label>
              <select v-model="project.client_type" class="form-select">
                <option value="standard">
                  {{ $t("project.info.clientTypes.standard") }}
                </option>
                <option value="installer">
                  {{ $t("project.info.clientTypes.installer") }}
                </option>
                <option value="distributor">
                  {{ $t("project.info.clientTypes.distributor") }}
                </option>
              </select>

              <!-- Discount dropdown (only for standard clients) -->
              <div v-if="project.client_type === 'standard'" class="mt-3">
                <label class="form-label">{{
                  $t("project.info.discount")
                }}</label>
                <select v-model="discountPercentage" class="form-select">
                  <option v-for="i in 26" :key="i - 1" :value="`${i - 1}%`">
                    {{ i - 1 }}%
                  </option>
                </select>
              </div>
            </div>
          </section>

          <!-- Address Section -->
          <section class="mb-3 pb-3 border-bottom">
            <h3 class="mb-3">
              {{ $t("project.info.address") }}
            </h3>
            <div class="mb-3">
              <input
                type="text"
                v-model="project.address"
                :placeholder="$t('project.info.streetAddress')"
                class="form-control mb-2"
              />
              <input
                type="text"
                v-model="project.city"
                :placeholder="$t('project.info.city')"
                class="form-control mb-2"
              />
              <input
                type="text"
                v-model="project.country"
                :placeholder="$t('project.info.country')"
                class="form-control mb-2"
              />
            </div>
          </section>

          <!-- Note Section -->
          <section class="mb-3">
            <h3 class="mb-3">{{ $t("project.info.note") }}</h3>
            <textarea
              v-model="project.note"
              rows="4"
              class="form-control"
            ></textarea>
          </section>
        </div>
      </div>

      <!-- Right Side Map -->
      <div class="col-12 col-md-9 col-lg-9 h-100 d-none d-md-block p-4">
        <div
          class="position-fixed h-100"
          style="top: 90px; left: 33%; right: 10px"
        >
          <MapComponent />
        </div>
      </div>

      <!-- Mobile Map View -->
      <div class="col-12 d-md-none p-4" style="height: 250px">
        <MapComponent />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed, watch } from "vue";
import MapComponent from "@/components/MapComponent.vue";
import { useProjectStore } from "@/stores/project";
import { storeToRefs } from "pinia";
import type { Project } from "@/types/projectTypes";
import { useI18n } from "vue-i18n";

export default defineComponent({
  name: "ProjectInfoComponent",
  components: {
    MapComponent,
  },
  setup() {
    const { t } = useI18n();
    const projectStore = useProjectStore();
    const { project } = storeToRefs(projectStore);

    // Ensure project is never null
    const projectData = computed(() => {
      if (!project.value) {
        return {
          project_name: "",
          temperature_min: "-10",
          temperature_avg: "45",
          temperature_max: "70",
          client_name: "",
          client_email: "",
          client_phone: "",
          client_type: "standard" as const,
          address: "",
          city: "",
          country: "",
          note: "",
          service_type: "product_supply" as const,
        } as Partial<Project>;
      }
      return project.value;
    });

    // Ref for discount percentage
    const discountPercentage = ref("0%");

    // Initialize discount percentage from project data
    const initializeDiscountPercentage = () => {
      if (project.value?.financial_data?.discount_percentage !== undefined) {
        const rawValue = project.value.financial_data.discount_percentage;
        const multipliedValue = rawValue * 100;
        const finalValue = `${multipliedValue}%`;
        console.log("Initializing discount percentage:", rawValue, "->", multipliedValue, "->", finalValue);
        console.log("Setting discountPercentage.value to:", finalValue);
        discountPercentage.value = finalValue;
        console.log("After setting, discountPercentage.value is:", discountPercentage.value);
      }
    };

    // Watch for changes in discount percentage
    watch(discountPercentage, (newValue) => {
      console.log("Discount percentage changed to:", newValue);
      // Update the project store when discount percentage changes
      if (project.value?.financial_data) {
        projectStore.$patch((state) => {
          if (state.project?.financial_data) {
            // Remove the % sign and convert to number
            const numericValue = Number(newValue.replace('%', ''));
            state.project.financial_data.discount_percentage = numericValue / 100;
          }
        });
      }
    });

    // Initialize default project data if none exists
    onMounted(() => {
      console.log("Project data on page load:", project.value);
      console.log("Discount percentage:", project.value?.financial_data?.discount_percentage);
      
      // Initialize discount percentage after project data is loaded
      initializeDiscountPercentage();
      
      // Debug: Log all dropdown options
      console.log("Dropdown options should be:");
      for (let i = 1; i <= 11; i++) {
        console.log(`Option ${i-1}: value="${i-1}%"`);
      }
      
      if (!project.value) {
        const defaultUser = {
          id: 0,
          username: "",
          email: "",
          first_name: "",
          last_name: "",
          is_active: true,
          is_staff: false,
          is_superuser: false,
          date_joined: new Date().toISOString(),
          last_login: null,
          groups: [],
          user_permissions: [],
          company_name: "",
        };

        project.value = {
          id: 0,
          user_id: 0,
          project_name: "",
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          client_id: "",
          client_name: "",
          client_email: "",
          client_phone: "",
          client_type: "standard",
          address: "",
          country: "",
          city: "",
          latitude: 0,
          longitude: 0,
          usable_area: 0,
          total_power: 0,
          total_battery: 0,
          total_price: 0,
          temperature_min: "-10",
          temperature_avg: "45",
          temperature_max: "70",
          devices: [],
          note: "",
          phase_type: "single",
          service_type: "complete_solution" as const,
          status: "draft",
          last_modified: new Date().toISOString(),
          map_state: {
            center: { lat: 0, lng: 0 },
            zoom: 10,
            screenshot_info: null,
          },
          drawing_data: {
            polygons: [],
            obstacles: [],
            panels: [],
            viewportTransform: { x: 0, y: 0, zoom: 1 },
            canvas_width_px: 0,
            canvas_height_px: 0,
            canvas_rotation: 0,
            zoom: 1,
            total_usable_area: 0,
            total_obstacle_area: 0,
            total_panels: 0,
          },
          consumption_data: {
            f1_data: [],
            f2_data: [],
            f3_data: [],
            grand_total: 0,
            is_yearly_data: false,
          },
          financial_data: {
            payment_type: "one_time",
            installment_ratio: 0,
            installment_duration_months: 0,
            import_energy_price: 0,
            export_energy_price: 0,
            annual_revenue: 0,
            base_price: 0,
            discount_amount: 0,
            discount_percentage: 0,
            cashback_amount: 0,
            cashback_percentage: 0,
            final_price: 0,
            currency: "EUR",
            vat_percentage: 19,
            vat_amount: 0,
            include_renew_inverter: false,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          },
        } as Project;
      } else if (
        project.value.id === 0 &&
        project.value.project_name.endsWith("_copy")
      ) {
        // This is a duplicated project, we don't need to do anything
        return project.value;
      }
      return project.value;
    });

    return {
      project: projectData,
      discountPercentage,
    };
  },
});
</script>

<style scoped>
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background-color: var(--color-light-gray);
  border-radius: 2px;
}

@media (max-width: 768px) {
  .container-fluid {
    padding-left: 60px; /* Account for fixed sidebar on mobile */
  }
}

.form-control,
.form-select,
.btn {
  border-radius: 2px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

.form-control:focus {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.form-control[disabled] {
  background-color: var(--color-light-gray);
  font-style: italic;
  color: var(--color-gray);
}

.navigation-buttons {
  position: sticky;
  bottom: 0;
  background-color: var(--color-white);
  padding: 1rem 0;
  border-top: 1px solid var(--color-light-gray);
}

.btn {
  padding: 0.5rem 1rem;
  font-weight: 500;
  transition: filter 0.2s;
}

.btn-primary {
  background-color: var(--primary-color);
  border-color: var(--primary-color);
}

.btn-primary:hover {
  background-color: var(--primary-color);
  border-color: var(--primary-color);
  filter: brightness(0.9);
}

.btn-secondary {
  background-color: var(--color-gray);
  border-color: var(--color-gray);
}

.btn-secondary:hover:not(:disabled) {
  background-color: var(--color-gray);
  border-color: var(--color-gray);
  filter: brightness(0.9);
}
</style>
